import { Button, Checkbox, DatePicker, Form, Input, Select, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import TopCard from "../../../../components/common/TopCard";
import Loader from "../../../../components/ui/Loader";
import { dateFormat } from "../../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import {
  useCreateKpiMutation,
  useGetSingleKpiQuery,
  useUpdateKpiMutation,
} from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { GetDepartmentsResponseType } from "../../../../types";
import { selectSearchOption } from "../../../../utils/utils";

const backUrl = "/admin/kpi-setup/open-kpi-setup";

export default function OpenKPISetupCreate() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { viewId, id } = useParams();
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: {
      limit: 0,
      skip: 0,
      is_published: 1,
    },
  });
  const [createKpi, { isLoading, isError, error, isSuccess }] =
    useCreateKpiMutation();
  const [
    updateKpi,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateKpiMutation();
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetSingleKpiQuery(String(viewId || id), {
    skip: !viewId && !id,
  });

  useError(isError, error, "KPI create fail");
  useError(isUpdateError, updateError, "KPI update fail");
  useError(isDetailsError, detailsError, "Get KPI details fail");
  useError(isDepartmentError, departmentError, "Get department data fail");

  const viewData = detailsData?.data;

  const onFinish = (values: any) => {
    const { cycle_time_start, cycle_time_end, departments, ...rest } = values;

    const newValues = {
      ...rest,
      cycle_time_start: cycle_time_start.toISOString(),
      cycle_time_end: cycle_time_end.toISOString(),
      departments: departments
        .filter((item: SelectedDepartment) => item.selected)
        .map((item: SelectedDepartment) => ({
          departmentId: item.id,
          due_date: item.due_date?.toISOString(),
        })),
    };

    if (id) {
      updateKpi({
        id: Number(id),
        body: newValues,
      });
    } else {
      createKpi(newValues);
    }
  };

  const selectedDepartment = Form.useWatch("departments", form)?.filter(
    (item: any) => item?.selected
  );

  const handleSelectAll = (checked: boolean) => {
    const departments = form.getFieldValue("departments") || [];

    const updated = departments.map((dept: any) => ({
      ...dept,
      selected: checked,
    }));

    form.setFieldsValue({ departments: updated });
  };

  const handleUnselectAll = () => {
    const departments = form.getFieldValue("departments") || [];

    const updated = departments.map((dept: any) => ({
      ...dept,
      selected: false,
    }));

    form.setFieldsValue({ departments: updated });
  };

  useEffect(() => {
    if (departmentData?.data?.departments?.length) {
      const departments = departmentData.data.departments.map((d: any) => ({
        id: d.id, // make sure `id` exists in your data
        selected: false,
        due_date: null,
      }));

      form.setFieldsValue({ departments });
    }
  }, [departmentData, form]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "KPI created successfully");
      navigate(backUrl);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "KPI updated successfully");
      navigate(backUrl);
    }
  }, [isUpdateSuccess, navigate]);

  useEffect(() => {
    if (viewId || id) {
      form.setFieldsValue({
        kpi_title: viewData?.kpi_title,
        plan_type: viewData?.plan_type,
        plan_period: viewData?.plan_period,
        cycle_time_start: viewData?.cycle_time_start
          ? dayjs(viewData?.cycle_time_start)
          : undefined,
        cycle_time_end: viewData?.cycle_time_end
          ? dayjs(viewData?.cycle_time_end)
          : undefined,
        departments: departmentData?.data?.departments?.map((item) => {
          const findData = viewData?.departments?.find(
            (el) => el?.departmentId === item?.id
          );

          if (findData) {
            return {
              due_date: findData?.due_date
                ? dayjs(findData?.due_date)
                : undefined,
              id: findData?.departmentId,
              selected: true,
            };
          }

          return {
            due_date: undefined,
            id: item?.id,
            selected: false,
          };
        }),
      });
    }
  }, [
    departmentData?.data?.departments,
    form,
    id,
    viewData?.cycle_time_end,
    viewData?.cycle_time_start,
    viewData?.departments,
    viewData?.kpi_title,
    viewData?.plan_period,
    viewData?.plan_type,
    viewId,
  ]);

  const planPeriod = Form.useWatch("plan_period", form);
  const cycleTimeStart = Form.useWatch("cycle_time_start", form);

  useEffect(() => {
    if (cycleTimeStart) {
      let endDate = cycleTimeStart;

      if (planPeriod === "half_yearly") {
        endDate = dayjs(cycleTimeStart).add(6, "month");
      } else if (planPeriod === "yearly") {
        endDate = dayjs(cycleTimeStart).add(12, "month");
      }

      form.setFieldsValue({
        cycle_time_end: endDate,
      });
    }
  }, [cycleTimeStart, form, planPeriod]);

  return (
    <SidebarLayout
      title={`${id ? "Update" : viewId ? "View" : "Open"} KPI Setup`}
    >
      <div className="max-w-[900px]">
        <TopCard
          leftChildren={
            <Link to={backUrl}>
              <Button className="normal-dark-button" icon={<BackButtonIcon />}>
                Back
              </Button>
            </Link>
          }
        >
          {/* <Button
            type="primary"
            className="middle-button !p-4"
            icon={<SettingIcon />}
          /> */}
        </TopCard>

        {isDetailsLoading ? (
          <Loader />
        ) : (
          <div className="rounded-md border border-extra-light bg-white p-4 mb-6">
            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinish}
              form={form}
            >
              <Form.Item
                name="kpi_title"
                label="KPI Title"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter KPI title",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter KPI title"
                  disabled={!!viewId}
                />
              </Form.Item>
              <div className="grid md:grid-cols-2 gap-x-4">
                <Form.Item
                  name="plan_type"
                  label="Plan Type"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please select plan type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    size="large"
                    allowClear
                    showSearch
                    filterOption={selectSearchOption}
                    options={planTypeList}
                    suffixIcon={<SelectDropdownLgIcon />}
                    disabled={!!viewId}
                  />
                </Form.Item>
                <Form.Item
                  name="plan_period"
                  label="Plan Period"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please select plan period",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    size="large"
                    allowClear
                    showSearch
                    filterOption={selectSearchOption}
                    options={planPeriodList}
                    suffixIcon={<SelectDropdownLgIcon />}
                    disabled={!!viewId}
                  />
                </Form.Item>
                <Form.Item
                  name="cycle_time_start"
                  label="Cycle Time Start"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please select cycle time start",
                    },
                  ]}
                >
                  <DatePicker
                    format={dateFormat}
                    suffixIcon={<DateIcon width={18} height={20} />}
                    style={{ width: "100%" }}
                    size="large"
                    disabled={!!viewId}
                    onChange={(e) => {
                      if (!e) {
                        form.setFieldsValue({ cycle_time_end: undefined });
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="cycle_time_end"
                  label="Cycle Time End"
                  className="mb-4"
                  validateFirst
                  rules={[
                    {
                      required: true,
                      message: "Please select cycle time end",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startDate = getFieldValue("cycle_time_start");
                        const period = getFieldValue("plan_period");

                        if (!startDate || !value) return Promise.resolve();

                        let minDate = dayjs(startDate);
                        if (period === "half_yearly") {
                          minDate = minDate.add(6, "month");
                        } else if (period === "yearly") {
                          minDate = minDate.add(12, "month");
                        }

                        if (value.isBefore(minDate, "day")) {
                          return Promise.reject(
                            new Error(
                              `Cycle Time End must be at least ${
                                period === "half_yearly"
                                  ? "6 months"
                                  : "12 months"
                              } after start date`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    format={dateFormat}
                    suffixIcon={<DateIcon width={18} height={20} />}
                    style={{ width: "100%" }}
                    size="large"
                    disabled={!!viewId || !cycleTimeStart}
                    // disabledDate={(current) => {
                    //   const startDate = form.getFieldValue("cycle_time_start");
                    //   return (
                    //     startDate &&
                    //     current &&
                    //     current.isBefore(startDate, "day")
                    //   );
                    // }}
                    disabledDate={(current) => {
                      const startDate = form.getFieldValue("cycle_time_start");
                      const period = form.getFieldValue("plan_period");

                      if (!startDate || !current) return false;

                      let maxDate = startDate;
                      if (period === "half_yearly") {
                        maxDate = dayjs(startDate).add(6, "month");
                      } else if (period === "yearly") {
                        maxDate = dayjs(startDate).add(12, "month");
                      }

                      return (
                        current.isBefore(startDate, "day") ||
                        current.isAfter(maxDate, "day")
                      );
                    }}
                    onChange={(e) => {
                      if (!e) {
                        form.setFieldsValue({ cycle_time_start: undefined });
                      }
                    }}
                  />
                </Form.Item>
              </div>

              {isDepartmentLoading ? (
                <Loader />
              ) : (
                <Form.List
                  name="departments"
                  rules={[{ validator: validateAtLeastOne }]}
                >
                  {(fields, _, errors) => {
                    return (
                      <div>
                        <div className="grid md:grid-cols-2 gap-x-4">
                          <TitleBar
                            title="Department"
                            leftChildren={
                              <div className="inline-flex items-center gap-2">
                                <Checkbox
                                  id="selectAll"
                                  checked={
                                    selectedDepartment?.length ===
                                    departmentData?.data?.departments?.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleSelectAll(e.target.checked);
                                    }
                                  }}
                                  disabled={
                                    !!viewId ||
                                    !departmentData?.data?.departments?.length
                                  }
                                />
                                <label
                                  htmlFor="selectAll"
                                  className="text-[#4A72FF] text-sm font-medium cursor-pointer"
                                >
                                  Select All
                                </label>
                              </div>
                            }
                          >
                            {selectedDepartment?.length ? (
                              <span
                                className="text-primary text-sm font-medium cursor-pointer"
                                onClick={handleUnselectAll}
                              >
                                Unselect
                              </span>
                            ) : null}
                          </TitleBar>

                          <TitleBar title="Due Date" />
                        </div>
                        <div className="space-y-4">
                          {fields.map(({ key, name, ...restField }, index) => (
                            <DepartmentRow
                              departments={
                                departmentData?.data?.departments || []
                              }
                              form={form}
                              index={index}
                              name={name}
                              restField={restField}
                              key={key}
                            />
                          ))}
                        </div>

                        {errors?.errors?.[0] && (
                          <div className="ant-form-item-explain-error mt-1.5 text-error">
                            {errors?.errors?.[0]}
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Form.List>
              )}

              {!viewId ? (
                <Space className="w-full mt-6" size={12}>
                  <Link to={backUrl}>
                    <Button
                      className="normal-dark-button modal-button"
                      size="middle"
                    >
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    className="modal-button"
                    type="primary"
                    htmlType="submit"
                    size="middle"
                    loading={isLoading || isUpdateLoading}
                    disabled={isDepartmentLoading}
                  >
                    {id ? "Update" : "Open Now"}
                  </Button>
                </Space>
              ) : null}
            </Form>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export const planTypeList = [
  { label: "Performance", value: "performance" },
  { label: "Development", value: "development" },
];

export const planPeriodList = [
  { label: "Half Yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
];

type TitleBarProps = {
  title: string;
  leftChildren?: ReactNode;
  children?: ReactNode;
};

const TitleBar = ({ title, leftChildren, children }: TitleBarProps) => {
  return (
    <div className="px-3 py-1.5 bg-slate-100 rounded-md flex justify-between items-center mb-4">
      <div className="flex items-center gap-3">
        <h4 className="text-display-body text-lg font-semibold">{title}</h4>
        {leftChildren}
      </div>

      {children}
    </div>
  );
};

const validateAtLeastOne = (_: any, value: any) => {
  const anySelected = value?.some((v: any) => v.selected);
  return anySelected
    ? Promise.resolve()
    : Promise.reject(new Error("Select at least one department."));
};

type DepartmentRowProps = {
  name: number;
  form: any;
  restField: any;
  departments: GetDepartmentsResponseType["data"]["departments"];
  index: number;
};

export type SelectedDepartment = {
  due_date: Dayjs | null;
  id: number;
  selected: boolean;
};

const DepartmentRow = ({
  name,
  form,
  restField,
  departments,
  index,
}: DepartmentRowProps) => {
  const { viewId } = useParams();
  const selected = Form.useWatch(["departments", name, "selected"], form);

  return (
    <div className="grid md:grid-cols-2 gap-x-4">
      {/* Checkbox */}
      <div>
        <Form.Item
          {...restField}
          name={[name, "selected"]}
          valuePropName="checked"
          className="border px-2.5 rounded-md border-display-medium hover:border-primary/80 transition-colors flex items-center mb-0"
        >
          <Checkbox
            onChange={() => {
              form.setFieldsValue({
                departments: form
                  .getFieldValue("departments")
                  .map((department: SelectedDepartment, index: number) =>
                    index === name
                      ? {
                          ...department,
                          due_date: undefined,
                        }
                      : department
                  ),
              });
            }}
            disabled={!!viewId}
          >
            {departments[index]?.name || "Department"}
          </Checkbox>
        </Form.Item>
      </div>

      {/* Due Date Picker */}
      <div>
        <Form.Item
          {...restField}
          name={[name, "due_date"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  getFieldValue(["departments", name, "selected"]) &&
                  !value
                ) {
                  return Promise.reject(new Error("Due date required"));
                }
                return Promise.resolve();
              },
            }),
          ]}
          className="mb-0"
        >
          <DatePicker
            format={dateFormat}
            suffixIcon={<DateIcon width={18} height={20} />}
            style={{ width: "100%" }}
            size="large"
            disabled={!selected || !!viewId}
            disabledDate={(current) => {
              const start = form.getFieldValue("cycle_time_start");
              const end = form.getFieldValue("cycle_time_end");

              if (current && current < dayjs().startOf("day")) {
                return true;
              }

              return (
                (start && current.isBefore(start, "day")) ||
                (end && current.isAfter(end, "day"))
              );
            }}
          />
        </Form.Item>
      </div>
    </div>
  );
};
